import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ADD_FAVOURITES_VIEW,
  MY_FAVOURITES_VIEW,
  ODDS_FILTER_OPTIONS,
} from "../../../../constants";
import Modal from "../../../../components/Modal";
import { FavouritesHeader } from "../../../../components/FavouritesHeader";
import FavouriteCard from "../../components/FavouriteCard";
import styles from "./MyFavourites.module.scss";
import { SET_ACTIVE_VIEW } from "../../../../constants/actions";
import AddIcon from "../../../../assets/icons/add_icon.svg";
import { sendAnalyticsEvent, sendMessage } from "../../../../lib/postMessage";
import { convertPositionToDecimal } from "../../../../lib/odds";
import {
  ALL,
  CLOSE,
  FAVOURITES,
  FAVOURITES_ANALYTICS_VIEW,
  OPEN,
} from "../../../../constants/analytics";

export function MyFavourites() {
  const dispatch = useDispatch();
  const { activeView, previousView } = useSelector((state) => state.view);
  const { storedFavourites } = useSelector((state) => state.filter);

  function findMatchingOddsOptions(minOdds, maxOdds) {
    return ODDS_FILTER_OPTIONS.filter((option) => {
      const minInRange = minOdds >= option.minOdds && minOdds <= option.maxOdds;
      const maxInRange = maxOdds >= option.minOdds && maxOdds <= option.maxOdds;
      return minInRange || maxInRange;
    });
  }

  function isOddsRangeSupported(odds) {
    if (!odds) return false;
    const hasIntegerOdds =
      Number.isInteger(odds.minOdds) || Number.isInteger(odds.maxOdds);
    if (!hasIntegerOdds) return true; // Already in supported format

    const minOddsIndex = ODDS_FILTER_OPTIONS.findIndex(
      (opt) => opt.minOdds === odds.minOdds
    );
    const maxOddsIndex = ODDS_FILTER_OPTIONS.findIndex(
      (opt) => opt.maxOdds === odds.maxOdds
    );

    return minOddsIndex !== -1 && maxOddsIndex !== -1;
  }

  // convert any favourites where the range is not supported
  // for example, favourites stored with the slider range
  // to the closest supported range
  useEffect(() => {
    if (activeView !== MY_FAVOURITES_VIEW) return;

    const updatedFavourites = storedFavourites.map((favourite) => {
      // check if the odds are already in a supported format
      if (!favourite.odds || isOddsRangeSupported(favourite.odds)) {
        return favourite;
      }

      // if odds are not in a supported format, convert them to a supported format
      // first treating them as positional values (legacy functionality) and then converting them to their corresponding decimal values
      const convertedOdds = {
        minOdds: convertPositionToDecimal(favourite.odds.minOdds),
        maxOdds: convertPositionToDecimal(favourite.odds.maxOdds),
      };
      const matchingOptions = findMatchingOddsOptions(
        convertedOdds.minOdds,
        convertedOdds.maxOdds
      );

      if (matchingOptions.length === 0) {
        return favourite;
      }

      return {
        ...favourite,
        odds: {
          minOdds: matchingOptions[0].minOdds,
          maxOdds: matchingOptions[matchingOptions.length - 1].maxOdds,
          isDecimal: true,
        },
      };
    });

    const hasUpdates = updatedFavourites.some(
      (favourite, index) => favourite !== storedFavourites[index]
    );

    if (hasUpdates) {
      sendMessage({
        type: "UpdateFavourites",
        payload: updatedFavourites,
      });
    }
  }, [activeView, storedFavourites]);

  if (activeView !== MY_FAVOURITES_VIEW) return null;

  const handleAddNewFavouriteClick = () => {
    dispatch({
      type: SET_ACTIVE_VIEW,
      payload: ADD_FAVOURITES_VIEW,
    });

    sendAnalyticsEvent({
      event: FAVOURITES,
      type: ALL,
      action: OPEN,
    });
  };

  function closeMyFavourites() {
    sendAnalyticsEvent({
      type: FAVOURITES_ANALYTICS_VIEW,
      payload: CLOSE,
    });
    dispatch({
      type: SET_ACTIVE_VIEW,
      payload: previousView,
    });
  }

  return (
    <Modal variant={MY_FAVOURITES_VIEW}>
      <FavouritesHeader heading="My Favourites" close={closeMyFavourites} />
      <div className={styles.favouritesContainer}>
        {storedFavourites.length > 0 && (
          <ul className={styles.list}>
            {storedFavourites.map((favourite) => (
              <FavouriteCard key={favourite.id} favourite={favourite} />
            ))}
          </ul>
        )}
        {storedFavourites.length < 10 && (
          <button
            type="button"
            className={styles.addFavourite}
            onClick={handleAddNewFavouriteClick}
          >
            <img src={AddIcon} alt="Add" />
            <span className={styles.addFavouriteText}>Add new</span>
          </button>
        )}
      </div>
    </Modal>
  );
}
